import { JobSearchCountType } from '@xing-com/crate-common-graphql-types';
import type { BoundedTotal, Maybe } from '@xing-com/crate-common-graphql-types';
import {
  SEARCH_PAGE_SIZE,
  SEARCH_STATE_PARAMS,
} from '@xing-com/crate-jobs-constants';
import { CITY } from '@xing-com/crate-jobs-domain-serps-constants';
import type { SerpsRouteParams } from '@xing-com/crate-jobs-domain-serps-helpers';

export const SERP_SEARCH_PAGE_SIZE = SEARCH_PAGE_SIZE;

type LegacyCountType = 'exact' | 'at_least';
type RawCountType = JobSearchCountType | LegacyCountType;

export type BoundedTotalWithCountType = Omit<BoundedTotal, 'countType'> & {
  countType?: RawCountType;
};

type NormalizeBoundedTotalResult = {
  total: number;
  countType: JobSearchCountType;
  isAtLeast: boolean;
  displayTotal: number;
};

type ClassicSerpPaginationArgs = {
  serps: SerpsRouteParams | null;
  isLoggedOut: boolean;
};

type TotalPageArgs = {
  boundedTotal?: Maybe<BoundedTotalWithCountType>;
  total?: number | null;
  pageSize: number;
};

type SerpPageUrlArgs = {
  pathname: string;
  page: number;
  search?: string;
};

export const normalizeBoundedTotal = (
  boundedTotal?: Maybe<BoundedTotalWithCountType>
): NormalizeBoundedTotalResult | null => {
  if (!boundedTotal) {
    return null;
  }

  const countType = normalizeCountType(boundedTotal);
  const isAtLeast = countType === JobSearchCountType.AtLeast;
  const atLeastTotal = boundedTotal.countType
    ? boundedTotal.total
    : boundedTotal.bound;

  return {
    total: boundedTotal.total,
    countType,
    isAtLeast,
    displayTotal: isAtLeast
      ? Math.max(0, atLeastTotal - 1)
      : boundedTotal.total,
  };
};

const normalizeCountType = ({
  bound,
  countType,
  total,
}: BoundedTotalWithCountType): JobSearchCountType => {
  if (countType === 'at_least') {
    return JobSearchCountType.AtLeast;
  }

  if (countType === 'exact') {
    return JobSearchCountType.Exact;
  }

  return (
    countType ??
    (total >= bound ? JobSearchCountType.AtLeast : JobSearchCountType.Exact)
  );
};

export const isClassicSerpPaginationEnabled = ({
  serps,
  isLoggedOut,
}: ClassicSerpPaginationArgs): boolean =>
  isLoggedOut && serps?.category === CITY && !serps.subcategory;

export const getSearchResultsPageSize = (
  args: ClassicSerpPaginationArgs
): number =>
  isClassicSerpPaginationEnabled(args)
    ? SERP_SEARCH_PAGE_SIZE
    : SEARCH_PAGE_SIZE;

export const getPaginationResultCount = ({
  boundedTotal,
  total,
}: Omit<TotalPageArgs, 'pageSize'>): number =>
  normalizeBoundedTotal(boundedTotal)?.total ?? total ?? 0;

export const getTotalPages = ({
  boundedTotal,
  total,
  pageSize,
}: TotalPageArgs): number => {
  if (pageSize <= 0) {
    return 0;
  }

  return Math.ceil(
    Math.max(0, getPaginationResultCount({ boundedTotal, total })) / pageSize
  );
};

export const getSerpPageUrl = ({
  pathname,
  page,
  search = '',
}: SerpPageUrlArgs): string => {
  const searchParameters = new URLSearchParams(search);
  searchParameters.delete(SEARCH_STATE_PARAMS.page);

  if (page > 1) {
    searchParameters.set(SEARCH_STATE_PARAMS.page, page.toString());
  }

  const searchString = searchParameters.toString().replaceAll('+', '%20');

  return searchString ? `${pathname}?${searchString}` : pathname;
};
